/**
 * Filter bar component for the audit logs table.
 */

import React, { useMemo, useCallback, useState, useEffect } from 'react';
import {
  Box,
  Select,
  DatePicker,
  Button,
  TextField,
  Stack,
  InlineStack,
  Icon,
} from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import type { CalendarDate } from '@internationalized/date';
import { today, getLocalTimeZone } from '@internationalized/date';
import {
  getActionOptions,
  getObjectTypeDisplayName,
  parseObjectTypeActionValue,
  getGroupedObjectTypeOptions,
  NO_OBJECT_ID_TYPES,
  type SelectOptionItem,
  type SelectOptionGroup,
} from '../../utils/object-type-mapping';
import { AUDIT_OBJECT_TYPE_TO_FIELDS } from '../../constants/audit-field-options';
import { formatAttributeName } from '../../utils/formatting';
import { getUserDisplayName } from '../../utils/user-formatting';
import {
  ALL_USERS_VALUE,
  ALL_OBJECT_TYPES_VALUE,
  getDefaultDateRange,
  buildFiltersFromValues,
  validateDateRangeForNonPremium,
  type FilterValue,
} from './filter-utils';
import { getAuditLogDayLimit } from '../../utils/access';
import type { AuditLogFilters } from '../../types';
import { isEmptyString } from '@/utils/string';
import { useAuth } from '@/services/auth';

/** Estimated height (px) of each row in virtualized Select dropdowns */
const SELECT_OPTION_HEIGHT_PX = 36;

/** Attributes to exclude from the filter dropdown */
const EXCLUDED_FILTER_ATTRIBUTES = new Set(['google_analytics_client_id']);

interface AuditLogsFiltersProps {
  filterValues: FilterValue[];
  setFilterValues: React.Dispatch<React.SetStateAction<FilterValue[]>>;
  onFiltersChange: (filters: Partial<AuditLogFilters>) => void;
  context: 'account' | 'workspace';
  allUsers: Array<{ user_id: number; email: string; first_name?: string; last_name?: string; deleted?: boolean }>;
  isLoadingUsers?: boolean;
  onLoadUsers?: () => void;
  campaigns?: Array<{ id: number; name: string }>;
  isLoadingCampaigns?: boolean;
  onLoadCampaigns?: () => void;
  segments?: Array<{ id: number; name: string }>;
  isLoadingSegments?: boolean;
  onLoadSegments?: () => void;
  newsletters?: Array<{ id: number; name: string }>;
  isLoadingNewsletters?: boolean;
  onLoadNewsletters?: () => void;
  transactionalMessages?: Array<{ id: number; name: string }>;
  isLoadingTransactionalMessages?: boolean;
  onLoadTransactionalMessages?: () => void;
  workspaces?: Array<{ id: number; name: string }>;
  roles?: Array<{ id: number; name: string }>;
}

export function AuditLogsFilters({
  filterValues,
  setFilterValues,
  onFiltersChange,
  context,
  allUsers,
  isLoadingUsers = false,
  onLoadUsers,
  campaigns = [],
  isLoadingCampaigns = false,
  onLoadCampaigns,
  segments = [],
  isLoadingSegments = false,
  onLoadSegments,
  newsletters = [],
  isLoadingNewsletters = false,
  onLoadNewsletters,
  transactionalMessages = [],
  isLoadingTransactionalMessages = false,
  onLoadTransactionalMessages,
  workspaces = [],
  roles = [],
}: AuditLogsFiltersProps) {
  const auth = useAuth();
  const dayLimit = getAuditLogDayLimit(auth.account);
  const hasUnlimitedAccess = dayLimit == null;

  // Get current filter values
  const currentDateRangeRaw = filterValues.find((f) => f.key === 'date_range')?.value as
    | { start?: CalendarDate; end?: CalendarDate }
    | undefined;
  const currentDateRange =
    currentDateRangeRaw?.start != null && currentDateRangeRaw?.end != null
      ? { start: currentDateRangeRaw.start, end: currentDateRangeRaw.end }
      : null;

  // Calculate minimum allowed date for accounts with limited access
  // "Last N days" includes today, so min allowed = today - (N - 1)
  const minAllowedDate = useMemo(() => {
    if (hasUnlimitedAccess) {
      return undefined;
    }
    const endDate = today(getLocalTimeZone());
    return endDate.subtract({ days: dayLimit - 1 });
  }, [hasUnlimitedAccess, dayLimit]);

  // Presets for date picker - accounts with limited access only see presets within their day limit
  const datePickerPresets = useMemo(() => {
    if (hasUnlimitedAccess) {
      // Accounts with unlimited access get all default presets (DatePicker will use its defaults when presets is undefined)
      return undefined;
    }

    const presets: Array<{
      type: 'relative';
      label: string;
      timeAgo: { value: number; unit: 'days' };
    }> = [];

    // Standard presets - show all that are within the limit
    // "Last 24 hours" (1 day)
    if (dayLimit >= 1) {
      presets.push({
        type: 'relative' as const,
        label: 'Last 24 hours',
        timeAgo: { value: 1, unit: 'days' as const },
      });
    }

    // "Last 7 days"
    if (dayLimit >= 7) {
      presets.push({
        type: 'relative' as const,
        label: 'Last 7 days',
        timeAgo: { value: 7, unit: 'days' as const },
      });
    }

    // "Last 30 days"
    if (dayLimit >= 30) {
      presets.push({
        type: 'relative' as const,
        label: 'Last 30 days',
        timeAgo: { value: 29, unit: 'days' as const },
      });
    }

    // Show custom limit preset only if it's not already one of the standard presets
    // Standard presets are: 1 day, 7 days, 30 days
    if (dayLimit !== 1 && dayLimit !== 7 && dayLimit !== 30) {
      presets.push({
        type: 'relative' as const,
        label: `Last ${dayLimit} days`,
        timeAgo: { value: dayLimit - 1, unit: 'days' as const },
      });
    }

    return presets;
  }, [hasUnlimitedAccess, dayLimit]);
  const currentUserId = (filterValues.find((f) => f.key === 'user_id')?.value as string) ?? ALL_USERS_VALUE;
  // Note: currentObjectTypeRaw may contain a combined value like "User:created" from URL params or row actions
  const currentObjectTypeRaw =
    (filterValues.find((f) => f.key === 'object_type')?.value as string) ?? ALL_OBJECT_TYPES_VALUE;
  // Extract just the object type from potentially combined format (e.g., "User:created" -> "User")
  const parsedObjectType = parseObjectTypeActionValue(currentObjectTypeRaw);
  const currentObjectType = parsedObjectType?.objectType ?? currentObjectTypeRaw;
  // Extract action from combined format or from separate action filter
  // When "Filter for related changes" is used, action is embedded in the combined format
  const currentAction =
    parsedObjectType?.action ?? (filterValues.find((f) => f.key === 'action')?.value as string) ?? '';
  const currentObjectId = (filterValues.find((f) => f.key === 'object_id')?.value as string) ?? '';
  const currentAttributeName = (filterValues.find((f) => f.key === 'attribute_name')?.value as string) ?? '';
  const currentIpAddress = (filterValues.find((f) => f.key === 'ip_addr')?.value as string) ?? '';
  const currentCampaignId = (filterValues.find((f) => f.key === 'campaign_id')?.value as string) ?? '';

  // Local state for text inputs
  const [objectIdInput, setObjectIdInput] = useState(currentObjectId);
  const [ipAddressInput, setIpAddressInput] = useState(currentIpAddress);

  // Sync local input state with filter values
  useEffect(() => {
    setObjectIdInput(currentObjectId);
  }, [currentObjectId]);

  useEffect(() => {
    setIpAddressInput(currentIpAddress);
  }, [currentIpAddress]);

  // Memoize default date range
  const defaultDateRange = useMemo(() => getDefaultDateRange(), []);

  // Object types that only have one action (created) - hide "Type of Change" dropdown
  const SINGLE_ACTION_OBJECT_TYPES = new Set(['JourneyExit', 'ProfileDeletion', 'ProfileMerge', 'ObjectDeletion']);

  // Object types with no meaningful attributes to filter - hide "Attribute Name" dropdown
  const NO_ATTRIBUTE_FILTER_OBJECT_TYPES = new Set(['JourneyExit', 'ProfileMerge', 'ObjectDeletion']);

  // Show filters only when a specific object type is selected (not "All Object Types")
  const hasSpecificObjectType = currentObjectType !== ALL_OBJECT_TYPES_VALUE;
  const showActionFilter = hasSpecificObjectType && !SINGLE_ACTION_OBJECT_TYPES.has(currentObjectType);
  const showAttributeFilter = hasSpecificObjectType && !NO_ATTRIBUTE_FILTER_OBJECT_TYPES.has(currentObjectType);
  const showObjectIdFilter = hasSpecificObjectType && !NO_OBJECT_ID_TYPES.has(currentObjectType);
  // Show campaign filter only for CampaignAction object type (workspace context only)
  const showCampaignFilter = context === 'workspace' && currentObjectType === 'CampaignAction';

  // Options for User Select dropdown - no icons on options, icon only on trigger
  const userSelectOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [{ value: ALL_USERS_VALUE, label: 'All team members' }];
    options.push({ value: '0', label: 'System' });

    for (const user of allUsers) {
      const name = getUserDisplayName(user);
      const label = name !== user.email ? `${name} (${user.email})` : user.email;
      options.push({
        value: user.user_id.toString(),
        label: user.deleted === true ? `${label} [Deleted]` : label,
      });
    }

    return options;
  }, [allUsers]);

  // Options for Object Type Select dropdown
  // Both contexts use grouped options with collapsible sections
  // The mixed array of flat option + groups is valid for Select's options prop
  // No icons on options, icon only on trigger
  const objectTypeSelectOptions = useMemo((): Array<SelectOptionItem | SelectOptionGroup> => {
    const groupedOptions = getGroupedObjectTypeOptions(context);
    const allTypesOption: SelectOptionItem = {
      value: ALL_OBJECT_TYPES_VALUE,
      label: 'All event types',
    };
    // Spread into array - Select component accepts both flat items and group items
    return [allTypesOption, ...groupedOptions];
  }, [context]);

  // Get action options based on selected object type
  const actionSelectOptions = useMemo(() => {
    if (!showActionFilter) return [];
    return [{ value: '', label: 'All actions' }, ...getActionOptions(currentObjectType)];
  }, [showActionFilter, currentObjectType]);

  // Dynamic label for Object ID filter
  const objectIdFilterLabel = useMemo(() => {
    if (currentObjectType === ALL_OBJECT_TYPES_VALUE) return 'OBJECT ID';
    if (currentObjectType === 'WorkspacePermissions') {
      return 'WORKSPACE ID';
    }
    // Special case: JourneyExit should show "JOURNEY ID" not "END JOURNEY ID"
    if (currentObjectType === 'JourneyExit') {
      return 'JOURNEY ID';
    }
    // Special cases for deletion/merge events - use simpler labels
    if (currentObjectType === 'ProfileDeletion' || currentObjectType === 'ProfileMerge') {
      return 'PROFILE ID';
    }
    if (currentObjectType === 'ObjectDeletion') {
      return 'OBJECT ID';
    }
    const displayName = getObjectTypeDisplayName(currentObjectType, context);
    return `${displayName.toUpperCase()} ID`;
  }, [currentObjectType, context]);

  // Determine if we should show entity name dropdown instead of ID text field
  // For workspace context: Campaign, Segment, Newsletter, TransactionalMessage
  // For account context: Workspace, Role
  const showEntityNameDropdown =
    (context === 'workspace' &&
      (currentObjectType === 'Campaign' ||
        currentObjectType === 'Segment' ||
        currentObjectType === 'Newsletter' ||
        currentObjectType === 'TransactionalMessage')) ||
    (context === 'account' && (currentObjectType === 'Workspace' || currentObjectType === 'Role'));

  // Get entity options for Campaign/Segment/Newsletter/Workspace/Role dropdown
  const entitySelectOptions = useMemo(() => {
    if (!showEntityNameDropdown) return [];

    let entities: Array<{ id: number; name: string }> = [];
    const entityLabel = currentObjectType.toLowerCase();

    if (currentObjectType === 'Campaign') {
      entities = campaigns;
    } else if (currentObjectType === 'Segment') {
      entities = segments;
    } else if (currentObjectType === 'Newsletter') {
      entities = newsletters;
    } else if (currentObjectType === 'TransactionalMessage') {
      entities = transactionalMessages;
    } else if (currentObjectType === 'Workspace') {
      entities = workspaces;
    } else if (currentObjectType === 'Role') {
      entities = roles;
    }

    return [
      { value: '', label: `All ${entityLabel}s` },
      ...entities.map((entity) => ({
        value: entity.id.toString(),
        label: `${entity.name} (#${entity.id})`,
      })),
    ];
  }, [
    showEntityNameDropdown,
    currentObjectType,
    campaigns,
    segments,
    newsletters,
    transactionalMessages,
    workspaces,
    roles,
  ]);

  // Get loading state for entity dropdown
  // Workspaces are loaded from auth context, so no loading state needed
  const isLoadingEntities = useMemo(() => {
    if (currentObjectType === 'Campaign') return isLoadingCampaigns;
    if (currentObjectType === 'Segment') return isLoadingSegments;
    if (currentObjectType === 'Newsletter') return isLoadingNewsletters;
    if (currentObjectType === 'TransactionalMessage') return isLoadingTransactionalMessages;
    // Workspaces are already loaded from auth context
    return false;
  }, [currentObjectType, isLoadingCampaigns, isLoadingSegments, isLoadingNewsletters, isLoadingTransactionalMessages]);

  // Handler to load entities when dropdown opens
  const handleEntityDropdownOpen = useCallback(() => {
    if (currentObjectType === 'Campaign' && onLoadCampaigns != null) {
      onLoadCampaigns();
    } else if (currentObjectType === 'Segment' && onLoadSegments != null) {
      onLoadSegments();
    } else if (currentObjectType === 'Newsletter' && onLoadNewsletters != null) {
      onLoadNewsletters();
    } else if (currentObjectType === 'TransactionalMessage' && onLoadTransactionalMessages != null) {
      onLoadTransactionalMessages();
    }
    // Workspaces don't need to be loaded - they're already available from auth context
  }, [currentObjectType, onLoadCampaigns, onLoadSegments, onLoadNewsletters, onLoadTransactionalMessages]);

  // Get available attribute options based on selected object type
  const attributeSelectOptions = useMemo(() => {
    if (!showAttributeFilter) return [];

    let attributes: readonly string[] = [];
    if (!isEmptyString(currentObjectType)) {
      attributes = AUDIT_OBJECT_TYPE_TO_FIELDS[currentObjectType as keyof typeof AUDIT_OBJECT_TYPE_TO_FIELDS] ?? [];
    }

    // Filter out excluded attributes
    const filteredAttributes = attributes.filter((attr) => !EXCLUDED_FILTER_ATTRIBUTES.has(attr));

    return [
      { label: 'All attributes', value: 'all' },
      ...filteredAttributes.map((attr) => ({
        label: formatAttributeName(attr),
        value: attr,
      })),
    ];
  }, [showAttributeFilter, currentObjectType]);

  // Memoize campaign select options for CampaignAction filter
  const campaignSelectOptions = useMemo(() => {
    if (!showCampaignFilter) return [];
    return [
      { value: '', label: 'All campaigns' },
      ...campaigns.map((campaign) => ({
        value: campaign.id.toString(),
        label: `${campaign.name} (#${campaign.id})`,
      })),
    ];
  }, [showCampaignFilter, campaigns]);

  // Filter handlers
  const handleFilterChange = useCallback(
    (key: string, value: unknown) => {
      let newValues: FilterValue[] = [...filterValues];

      // DEPENDENCY LOGIC: When object_type changes, clear dependent filters
      // This prevents invalid filter combinations (e.g., Newsletter attributes with Campaign selected)
      if (key === 'object_type') {
        // Clear attribute_name: Different object types have different valid attributes
        // Clear action: Different object types have different valid actions (e.g., JourneyExit only has 'created')
        // Clear campaign_id: Only applicable for CampaignAction object type
        newValues = newValues.filter(
          (f) => f.key !== 'attribute_name' && f.key !== 'action' && f.key !== 'campaign_id',
        );

        // Special case: "All object types" - clear object_id since it's meaningless without a specific type
        if (value === ALL_OBJECT_TYPES_VALUE) {
          newValues = newValues.filter((f) => f.key !== 'object_id');
          setObjectIdInput(''); // Also clear the text input state
        }
      }

      // DATE RANGE VALIDATION: For accounts with limited access, enforce day limit
      // This validation is also done in buildFiltersFromValues, but we do it here too
      // to ensure the UI state reflects the clamped values (including partial date ranges)
      if (key === 'date_range' && !hasUnlimitedAccess) {
        // Runtime type validation
        if (typeof value !== 'object' || value === null) {
          return; // Skip invalid date range
        }
        const dateRange = value as { start?: CalendarDate; end?: CalendarDate };
        // Use validateDateRangeForNonPremium to handle all cases:
        // - Both dates provided
        // - Only start date provided (clamps start, sets end to today)
        // - Only end date provided (clamps end, sets start appropriately)
        // - Both null (returns as-is)
        // This ensures UI state is always consistent with API filters
        const validatedRange = validateDateRangeForNonPremium(dateRange, dayLimit);
        value = validatedRange;
      }

      // Update or add the filter value
      const existingIndex = newValues.findIndex((f) => f.key === key);
      if (existingIndex !== -1) {
        // Filter already exists - update its value
        newValues[existingIndex] = { key, value };
      } else {
        // Filter doesn't exist - add it
        newValues = [...newValues, { key, value }];
      }

      // Compute filters immediately from the new values
      // This converts FilterValue[] (UI state) to AuditLogFilters (API format)
      const newFilters = buildFiltersFromValues(newValues, dayLimit);

      // When object_type changes, explicitly clear dependent filters from URL
      // buildFiltersFromValues only sets values that exist, so we need to explicitly set undefined
      if (key === 'object_type') {
        newFilters.attribute_name = undefined;
        newFilters.action = undefined;
        newFilters.campaign_id = undefined;
      }

      // TWO-STEP UPDATE: Update both local state and notify parent
      // 1. Local state: Keep UI in sync
      setFilterValues(newValues);
      // 2. Parent callback: Trigger API refetch with new filters
      onFiltersChange(newFilters);
    },
    [filterValues, setFilterValues, onFiltersChange, hasUnlimitedAccess, dayLimit],
  );

  const handleFilterClear = useCallback(
    (key: string) => {
      if (key === 'date_range') {
        setFilterValues((prev) => {
          const newValues = prev.filter((f) => f.key !== key);
          newValues.push({ key: 'date_range', value: getDefaultDateRange() });
          return newValues;
        });
        const defaultRange = getDefaultDateRange();
        onFiltersChange({
          start_time: `${defaultRange.start.toString()}T00:00:00Z`,
          end_time: `${defaultRange.end.toString()}T23:59:59Z`,
          cursor: undefined,
        });
      } else if (key === 'user_id') {
        setFilterValues((prev) => {
          const newValues = prev.filter((f) => f.key !== key);
          newValues.push({ key: 'user_id', value: ALL_USERS_VALUE });
          return newValues;
        });
        onFiltersChange({ user_id: undefined, cursor: undefined });
      } else if (key === 'object_type') {
        setFilterValues((prev) => {
          const newValues = prev.filter(
            (f) =>
              f.key !== key &&
              f.key !== 'action' &&
              f.key !== 'attribute_name' &&
              f.key !== 'object_id' &&
              f.key !== 'campaign_id',
          );
          newValues.push({ key: 'object_type', value: ALL_OBJECT_TYPES_VALUE });
          return newValues;
        });
        setObjectIdInput('');
        onFiltersChange({
          object_type: undefined,
          action: undefined,
          attribute_name: undefined,
          object_id: undefined,
          campaign_id: undefined,
          cursor: undefined,
        });
      } else {
        setFilterValues((prev) => prev.filter((f) => f.key !== key));
        onFiltersChange({ [key]: undefined, cursor: undefined });
      }
    },
    [setFilterValues, onFiltersChange],
  );

  const handleFilterClearAll = useCallback(() => {
    const defaultRange = getDefaultDateRange();
    setFilterValues([
      { key: 'date_range', value: defaultRange },
      { key: 'user_id', value: ALL_USERS_VALUE },
      { key: 'object_type', value: ALL_OBJECT_TYPES_VALUE },
    ]);
    setObjectIdInput('');
    setIpAddressInput('');
    onFiltersChange({
      user_id: undefined,
      ip_addr: undefined,
      object_type: undefined,
      object_id: undefined,
      attribute_name: undefined,
      action: undefined,
      campaign_id: undefined,
      start_time: `${defaultRange.start.toString()}T00:00:00Z`,
      end_time: `${defaultRange.end.toString()}T23:59:59Z`,
      cursor: undefined,
    });
  }, [setFilterValues, onFiltersChange]);

  // Helper to create text field filter handlers
  const createTextFieldHandlers = useCallback(
    (filterKey: string, inputValue: string, currentValue: string, setInputValue: (value: string) => void) => {
      const applyFilter = () => {
        if (inputValue !== currentValue) {
          if (inputValue === '') {
            handleFilterClear(filterKey);
          } else {
            handleFilterChange(filterKey, inputValue);
          }
        }
      };

      return {
        onClear: () => {
          setInputValue('');
          handleFilterClear(filterKey);
        },
        onInput: (e: Event & { target: HTMLInputElement }) => {
          setInputValue(e.target.value);
        },
        onBlur: applyFilter,
        onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            applyFilter();
          }
        },
      };
    },
    [handleFilterChange, handleFilterClear],
  );

  const objectIdHandlers = createTextFieldHandlers('object_id', objectIdInput, currentObjectId, setObjectIdInput);
  const ipAddressHandlers = createTextFieldHandlers('ip_addr', ipAddressInput, currentIpAddress, setIpAddressInput);

  // Check if any filters are applied (not default values)
  const hasActiveFilters =
    currentUserId !== ALL_USERS_VALUE ||
    currentObjectType !== ALL_OBJECT_TYPES_VALUE ||
    !isEmptyString(currentObjectId) ||
    !isEmptyString(currentAttributeName) ||
    !isEmptyString(currentIpAddress) ||
    (currentDateRange != null &&
      (currentDateRange.start.toString() !== defaultDateRange.start.toString() ||
        currentDateRange.end.toString() !== defaultDateRange.end.toString()));

  // Check if any nested filters should be shown
  const hasNestedFilters = showActionFilter || showAttributeFilter || showObjectIdFilter || showCampaignFilter;

  return (
    <Stack gap="200" mt="200">
      {/* Core filters row */}
      <Box display="flex" alignItems="flex-end" flexWrap="wrap" gap="200">
        <Box style={{ minWidth: '280px' }}>
          <DatePicker
            label="DATE RANGE"
            isRange={true}
            withPresets={true}
            presets={datePickerPresets}
            value={currentDateRange}
            minDate={minAllowedDate}
            onChange={(value) => {
              handleFilterChange('date_range', value);
            }}
          />
        </Box>

        <Box style={{ minWidth: '300px' }}>
          <Select
            label="TEAM MEMBER"
            value={currentUserId}
            onChange={(value) => {
              if (value != null) {
                handleFilterChange('user_id', value);
              }
            }}
            options={userSelectOptions}
            placeholder="Select a user"
            isSearchable={true}
            onOpen={onLoadUsers}
            isLoading={isLoadingUsers}
            withVirtualizer={true}
            virtualizerOptions={{ estimateSize: () => SELECT_OPTION_HEIGHT_PX, overscan: 5 }}
            valueComponent={({ option }: { option?: SelectOptionItem }) => (
              <InlineStack alignBlock="center" gap="100">
                <Icon name="person" size="sm" />
                <span>{option?.label ?? ''}</span>
              </InlineStack>
            )}
          />
        </Box>

        <Box style={{ minWidth: '200px' }}>
          <TextField
            label="IP ADDRESS"
            value={ipAddressInput}
            placeholder="e.g., 192.168.1.1"
            icon="browser"
            isClearable={ipAddressInput !== ''}
            {...ipAddressHandlers}
          />
        </Box>

        <Box style={{ minWidth: '200px' }}>
          <Select
            label="EVENT TYPE"
            value={currentObjectType === ALL_OBJECT_TYPES_VALUE ? ALL_OBJECT_TYPES_VALUE : currentObjectType}
            onChange={(value) => {
              if (value != null) {
                // When user selects from dropdown, value is just the object type (not combined)
                // This clears any previously combined value from row actions
                handleFilterChange('object_type', value);
              }
            }}
            options={objectTypeSelectOptions}
            placeholder="Select a type"
            isSearchable={true}
            valueComponent={({ option }: { option?: SelectOptionItem }) => (
              <InlineStack alignBlock="center" gap="100">
                <Icon name="trigger-event" size="sm" />
                <span>{option?.label ?? ''}</span>
              </InlineStack>
            )}
          />
        </Box>

        {hasActiveFilters ? (
          <Button variant="tertiary" icon="close" onClick={handleFilterClearAll}>
            Reset all filters
          </Button>
        ) : null}
      </Box>

      {/* Nested filters row - shown when an event type is selected */}
      {hasNestedFilters ? (
        <Box
          display="inline-flex"
          alignItems="flex-end"
          flexWrap="wrap"
          gap="200"
          p="200"
          style={{
            backgroundColor: themeVars.palette['grey-100'],
            borderRadius: themeVars.radius['6'],
            width: 'fit-content',
          }}
        >
          {showObjectIdFilter && showEntityNameDropdown ? (
            <Box style={{ minWidth: '200px' }}>
              <Select
                label={currentObjectType.toUpperCase()}
                value={currentObjectId}
                onChange={(value) => {
                  if (value != null) {
                    if (value === '') {
                      handleFilterClear('object_id');
                      setObjectIdInput('');
                    } else {
                      handleFilterChange('object_id', value);
                      setObjectIdInput(value);
                    }
                  }
                }}
                options={entitySelectOptions}
                placeholder={`Select ${currentObjectType.toLowerCase()}`}
                isSearchable={true}
                onOpen={handleEntityDropdownOpen}
                isLoading={isLoadingEntities}
              />
            </Box>
          ) : showObjectIdFilter && currentObjectType !== 'Account' ? (
            <Box style={{ minWidth: '130px' }}>
              <TextField
                label={objectIdFilterLabel}
                value={objectIdInput}
                placeholder="Enter ID"
                isClearable={objectIdInput !== ''}
                {...objectIdHandlers}
              />
            </Box>
          ) : null}

          {showActionFilter ? (
            <Box style={{ minWidth: '150px' }}>
              <Select
                label="TYPE OF CHANGE"
                value={currentAction}
                onChange={(value) => {
                  if (value != null) {
                    if (value === '') {
                      handleFilterClear('action');
                    } else {
                      handleFilterChange('action', value);
                    }
                  }
                }}
                options={actionSelectOptions}
                placeholder="All actions"
                isSearchable={false}
              />
            </Box>
          ) : null}

          {showAttributeFilter ? (
            <Box style={{ minWidth: '180px' }}>
              <Select
                label="ATTRIBUTE NAME"
                value={isEmptyString(currentAttributeName) ? 'all' : currentAttributeName}
                onChange={(value) => {
                  if (value != null) {
                    if (value === 'all') {
                      handleFilterClear('attribute_name');
                    } else {
                      handleFilterChange('attribute_name', value);
                    }
                  }
                }}
                options={attributeSelectOptions}
                placeholder="Select attribute"
                isSearchable={true}
              />
            </Box>
          ) : null}

          {showCampaignFilter ? (
            <Box style={{ minWidth: '200px' }}>
              <Select
                label="CAMPAIGN"
                value={currentCampaignId}
                onChange={(value) => {
                  if (value != null) {
                    if (value === '') {
                      handleFilterClear('campaign_id');
                    } else {
                      handleFilterChange('campaign_id', value);
                    }
                  }
                }}
                options={campaignSelectOptions}
                placeholder="Select campaign"
                isSearchable={true}
                onOpen={onLoadCampaigns}
                isLoading={isLoadingCampaigns}
              />
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Stack>
  );
}
